import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';

const CATEGORIES = ['art', 'watches', 'handbags', 'electronics', 'clothing', 'collectibles', 'automotive', 'jewelry'];
const PROMPT_TYPES = [
  { value: 'deep_auth', label: 'Deep Auth' },
  { value: 'fast_identify_supplement', label: 'FastIdentify Supplement' },
];

export function PromptPlaygroundPage() {
  const { idToken } = useAuth();
  const api = createApiClient(idToken!);
  const queryClient = useQueryClient();

  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);
  const [selectedPromptType, setSelectedPromptType] = useState('deep_auth');
  const [editText, setEditText] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState<'draft' | 'active' | 'archived'>('draft');
  const [showCodeDefault, setShowCodeDefault] = useState(false);

  // Fetch all overrides
  const { data: overridesData } = useQuery({
    queryKey: ['prompt-overrides'],
    queryFn: () => api.listPromptOverrides(),
  });

  // Fetch code defaults for selected category
  const { data: codePromptsData } = useQuery({
    queryKey: ['code-prompts', selectedCategory],
    queryFn: () => api.getCodePrompts(selectedCategory),
    enabled: !!selectedCategory,
  });

  // Fetch current override for selected category/type
  const { data: currentOverride } = useQuery({
    queryKey: ['prompt-override', selectedCategory, selectedPromptType],
    queryFn: () => api.getPromptOverride(selectedCategory, selectedPromptType),
    enabled: !!selectedCategory && !!selectedPromptType,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api.savePromptOverride({
        categoryId: selectedCategory,
        promptType: selectedPromptType,
        promptText: editText,
        status: editStatus,
        notes: editNotes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-override', selectedCategory, selectedPromptType] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deletePromptOverride(selectedCategory, selectedPromptType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-override', selectedCategory, selectedPromptType] });
      setEditText('');
      setEditNotes('');
    },
  });

  // Get code default for current selection
  const codeDefault = codePromptsData?.prompts?.[selectedPromptType] || '';

  // Load override into editor
  const loadOverride = () => {
    if (currentOverride?.override) {
      setEditText(currentOverride.override.promptText);
      setEditNotes(currentOverride.override.notes || '');
      setEditStatus(currentOverride.override.status || 'draft');
    }
  };

  // Load code default into editor
  const loadCodeDefault = () => {
    setEditText(codeDefault);
    setEditNotes('Loaded from code default');
    setEditStatus('draft');
  };

  // Build override status map for the grid
  const overrideMap: Record<string, any> = {};
  if (overridesData?.overrides) {
    for (const o of overridesData.overrides) {
      const key = `${o.categoryId}:${o.promptType}`;
      overrideMap[key] = o;
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-white">Prompt Playground</h1>

      {/* Override Status Grid */}
      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <h2 className="text-sm font-medium text-slate-400 mb-3">Override Status</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pb-2 pr-4">Category</th>
                <th className="pb-2 pr-4">Deep Auth</th>
                <th className="pb-2">FastIdentify Supplement</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((cat) => (
                <tr key={cat} className="border-t border-slate-800">
                  <td className="py-2 pr-4 text-white capitalize">{cat}</td>
                  {['deep_auth', 'fast_identify_supplement'].map((pt) => {
                    const override = overrideMap[`${cat}:${pt}`];
                    return (
                      <td key={pt} className="py-2 pr-4">
                        {override ? (
                          <button
                            onClick={() => {
                              setSelectedCategory(cat);
                              setSelectedPromptType(pt);
                            }}
                            className={`px-2 py-0.5 text-xs rounded cursor-pointer ${
                              override.status === 'active'
                                ? 'bg-green-900/50 text-green-300'
                                : override.status === 'draft'
                                ? 'bg-amber-900/50 text-amber-300'
                                : 'bg-slate-800 text-slate-400'
                            }`}
                          >
                            {override.status} v{override.version}
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedCategory(cat);
                              setSelectedPromptType(pt);
                            }}
                            className="text-xs text-slate-600 hover:text-slate-400"
                          >
                            code default
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Editor */}
      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-sm font-medium text-slate-400">Editor</h2>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-slate-800 text-white text-sm rounded px-2 py-1 border border-slate-700"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={selectedPromptType}
            onChange={(e) => setSelectedPromptType(e.target.value)}
            className="bg-slate-800 text-white text-sm rounded px-2 py-1 border border-slate-700"
          >
            {PROMPT_TYPES.map((pt) => (
              <option key={pt.value} value={pt.value}>
                {pt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={loadCodeDefault}
            className="px-3 py-1.5 text-xs bg-slate-800 text-slate-300 rounded hover:bg-slate-700"
          >
            Load Code Default
          </button>
          {currentOverride?.override && (
            <button
              onClick={loadOverride}
              className="px-3 py-1.5 text-xs bg-blue-900/50 text-blue-300 rounded hover:bg-blue-900/70"
            >
              Load Current Override (v{currentOverride.override.version})
            </button>
          )}
          <button
            onClick={() => setShowCodeDefault(!showCodeDefault)}
            className="px-3 py-1.5 text-xs bg-slate-800 text-slate-400 rounded hover:bg-slate-700"
          >
            {showCodeDefault ? 'Hide' : 'Show'} Code Default
          </button>
        </div>

        {/* Code default reference */}
        {showCodeDefault && codeDefault && (
          <div className="mb-4">
            <div className="text-xs text-slate-500 mb-1">Code Default (read-only)</div>
            <pre className="text-xs text-slate-400 bg-slate-950 border border-slate-800 rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
              {codeDefault}
            </pre>
          </div>
        )}

        {/* Edit area */}
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          placeholder="Enter prompt text..."
          rows={16}
          className="w-full bg-slate-950 text-slate-300 text-sm border border-slate-800 rounded p-3 font-mono resize-y focus:border-orange-500 focus:outline-none"
        />

        <div className="flex gap-4 mt-3">
          <div className="flex-1">
            <label className="text-xs text-slate-500 block mb-1">Notes</label>
            <input
              type="text"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="What changed and why..."
              className="w-full bg-slate-950 text-slate-300 text-sm border border-slate-800 rounded px-3 py-1.5 focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Status</label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as any)}
              className="bg-slate-800 text-white text-sm rounded px-2 py-1.5 border border-slate-700"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Save/Delete buttons */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!editText.trim() || saveMutation.isPending}
            className="px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Override'}
          </button>
          {currentOverride?.override && (
            <button
              onClick={() => {
                if (confirm('Delete this override? The code default will be used instead.')) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm bg-red-900/50 text-red-300 rounded hover:bg-red-900/70 disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Override'}
            </button>
          )}
        </div>

        {saveMutation.isSuccess && (
          <div className="mt-2 text-xs text-green-400">Override saved successfully.</div>
        )}
        {saveMutation.isError && (
          <div className="mt-2 text-xs text-red-400">Error: {(saveMutation.error as Error).message}</div>
        )}

        {/* Current override info */}
        {currentOverride?.override && (
          <div className="mt-4 pt-3 border-t border-slate-800 text-xs text-slate-500 space-y-1">
            <div>Current override: v{currentOverride.override.version} ({currentOverride.override.status})</div>
            <div>Updated: {new Date(currentOverride.override.updatedAt).toLocaleString()}</div>
            {currentOverride.override.notes && <div>Notes: {currentOverride.override.notes}</div>}
          </div>
        )}
      </section>
    </div>
  );
}
